import React, {useState, useId, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, useMediaQuery, useStyleConfig} from '@chakra-ui/react'
import {Pagination, Keyboard, FreeMode, Navigation} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import PromoSlideVideoCard from './partials/promo-slide-video-card'
import {breakpoints, remCalc} from '../../../theme/_utils'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {GalleryChevronLeftIcon, GalleryChevronRightIcon} from '../../icons'

export const PromoSliderVideo = ({
    sliderGroup,
    title,
    abTitle = '',
    isVariantStyle = false,
    contentType = '',
    currentVideoSlide = noop,
}) => {
    const uniqueId = useId().replace(/:/g, '')
    const sliderId = `promo-slider-video-${uniqueId}`
    const swiperStyles = useStyleConfig('Swiper', {
        variant: isVariantStyle && 'paperlessVariant',
    })
    const styles = useStyleConfig('PromoSlider', {
        variant: isVariantStyle && 'paperlessVariant',
    })
    const [isPlaying, setIsPlaying] = useState(false)
    const [activeSlideIndex, setActiveSlideIndex] = useState(0)

    const [isDesktop] = useMediaQuery(`(min-width: ${breakpoints.lg})`, {fallback: true})
    const isMobile = !isDesktop

    const swiperRef = useRef(null)
    const prevButtonRef = useRef(null)
    const nextButtonRef = useRef(null)
    const navigationDirection = useRef(null)

    // Swiper requires at least slidesPerView + slidesPerGroup slides in loop mode.
    const sourceSliderGroup = sliderGroup || []
    const duplicateCount = sourceSliderGroup.length
        ? Math.max(2, Math.ceil(4 / sourceSliderGroup.length))
        : 0
    const extendedSliderGroup = Array.from({length: duplicateCount}, () => sourceSliderGroup).flat()

    const slidesToView = {
        base: 1,
        md: 1.5,
        lg: 2.25,
        xl: 2.75,
    }

    useEffect(() => {
        function updateBulletTabIndex() {
            const mobile = window.innerWidth < 1024
            document
                .querySelectorAll(`#${sliderId} .promo-slider-pagination button`)
                .forEach((btn) => {
                    btn.tabIndex = mobile ? 0 : -1
                })
        }

        function handleResize() {
            updateBulletTabIndex()
        }

        updateBulletTabIndex()
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    useEffect(() => {
        if (extendedSliderGroup[activeSlideIndex]) {
            currentVideoSlide({
                title: extendedSliderGroup[activeSlideIndex]?.text_block?.title || '',
                source: extendedSliderGroup[activeSlideIndex]?.video_link?.href || '',
                thumbnail: extendedSliderGroup[activeSlideIndex]?.thumbnail?.src || '',
                slideIndex: activeSlideIndex,
                isPlaying: isPlaying,
            })
        }
    }, [activeSlideIndex, isPlaying])

    if (extendedSliderGroup.length === 0) return null

    return (
        <Box data-ab={abTitle} data-cs-label={abTitle} id={sliderId}>
            {/* Hide h2 if title is empty or only contains whitespace; title may also be a React node */}
            {(typeof title === 'string' ? title.trim() !== '' : !!title) && (
                <Text as="h2" {...styles.videoTitle}>
                    {title}
                </Text>
            )}
            <Box
                __css={{...swiperStyles, ...styles.container}}
                py={{base: remCalc(16), md: remCalc(40)}}
                data-testid="promo-slider-video-container"
            >
                <Box position="relative">
                    <HideOnMobile>
                        {/* note chevronIcon is used all swipers thats why top is being overwritten*/}
                        {/* left/right center the 36px arrow in the slide gap: 50% - (slideWidth/2 + spaceBetween/2 + 18px) for each swiper breakpoint */}
                        <Flex
                            {...styles.heroLeftRightArrowsContainer}
                            top={{md: 'calc(50% - 60px)', lg: '50%'}}
                            sx={{
                                '@media (min-width: 1024px) and (max-width: 1431px)': {
                                    left: 'calc(27.78% - 31.33px)',
                                },
                                '@media (min-width: 1432px)': {
                                    left: 'calc(50% - 296.55px)',
                                },
                            }}
                        >
                            <Flex
                                {...styles.heroLeftRightArrows}
                                ref={prevButtonRef}
                                className={`swiper-button-prev swiper-button-prev-${uniqueId}`}
                                data-testid="swiper-button-prev"
                                tabIndex={0}
                                role="button"
                                aria-label="swiper button prev icon"
                                onClick={() => {
                                    navigationDirection.current = 'prev'
                                    swiperRef.current?.slidePrev()
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        navigationDirection.current = 'prev'
                                        swiperRef.current?.slidePrev()
                                    }
                                }}
                            >
                                <GalleryChevronLeftIcon boxSize={remCalc(16)} />
                            </Flex>
                        </Flex>
                    </HideOnMobile>

                    <Swiper
                        key="promo-slider-video"
                        modules={[Navigation, Pagination, Keyboard, FreeMode]}
                        navigation={{
                            prevEl: `.swiper-button-prev-${uniqueId}`,
                            nextEl: `.swiper-button-next-${uniqueId}`,
                        }}
                        pagination={{
                            clickable: true,
                            el: `#${sliderId} .promo-slider-pagination`,
                            renderBullet: (index, className) => {
                                // Render bullets only for original slides length
                                if (index >= sliderGroup.length) return ''
                                return `<button class="${className}" aria-label="slide ${
                                    index + 1
                                }" tabindex="-1"></button>`
                            },
                        }}
                        keyboard={{
                            enabled: true,
                            onlyInViewport: false,
                            pageUpDown: false,
                        }}
                        loop={true}
                        centeredSlides={true}
                        freeMode={isMobile}
                        tabIndex={-1}
                        spaceBetween={16}
                        onInit={(swiper) => {
                            swiperRef.current = swiper
                            swiper.slideToLoop(0, 0, false)
                            const bullets = document.querySelectorAll(
                                `#${sliderId} .promo-slider-pagination .swiper-pagination-bullet`,
                            )
                            bullets.forEach((bullet) => {
                                bullet.setAttribute('tabindex', '-1')
                            })

                            swiper.slides.forEach((slide, idx) => {
                                const isActive = idx === swiper.activeIndex
                                const wrapper = slide.querySelector(
                                    '[data-testid="promo-slide-video-container"]',
                                )
                                if (wrapper) {
                                    wrapper.setAttribute('tabindex', isActive ? '0' : '-1')
                                    const allDescendants = wrapper.querySelectorAll('*')
                                    allDescendants.forEach((el) => {
                                        el.setAttribute('tabindex', '-1')
                                    })
                                }
                            })
                        }}
                        onSlideChange={(swiper) => {
                            //since we have duplicate slides we need to keep track of the index that corespondes to the real position
                            const originalIndex = swiper.realIndex % sliderGroup.length
                            document
                                .querySelectorAll(`#${sliderId} .promo-slider-pagination button`)
                                .forEach((bullet, idx) => {
                                    bullet.classList.toggle(
                                        'swiper-pagination-bullet-active',
                                        idx === originalIndex,
                                    )
                                })

                            swiper.slides.forEach((slide, idx) => {
                                const isActive = idx === swiper.activeIndex
                                const wrapper = slide.querySelector(
                                    '[data-testid="promo-slide-video-container"]',
                                )
                                if (wrapper) {
                                    wrapper.setAttribute('tabindex', isActive ? '0' : '-1')
                                    const allDescendants = wrapper.querySelectorAll('*')
                                    allDescendants.forEach((el) => {
                                        el.setAttribute('tabindex', '-1')
                                    })
                                }
                            })

                            if (navigationDirection.current) {
                                setTimeout(() => {
                                    const targetSlide = swiper.slides[swiper.activeIndex]
                                    if (targetSlide) {
                                        const wrapper = targetSlide.querySelector(
                                            '[data-testid="promo-slide-video-container"]',
                                        )
                                        if (wrapper) {
                                            wrapper.focus()
                                        }
                                    }
                                    navigationDirection.current = null
                                }, 150)
                            }
                        }}
                        onTransitionEnd={(swiper) => setActiveSlideIndex(swiper.activeIndex)}
                        breakpoints={{
                            0: {
                                spaceBetween: 16,
                                slidesPerView: slidesToView.base,
                                centeredSlides: true,
                                centeredSlidesBounds: false,
                                freeMode: true,
                            },
                            768: {
                                spaceBetween: 16,
                                slidesPerView: slidesToView.md,
                                centeredSlides: true,
                                centeredSlidesBounds: false,
                                freeMode: true,
                            },
                            1024: {
                                slidesPerView: slidesToView.lg,
                                spaceBetween: 60,
                                centeredSlides: true,
                                centeredSlidesBounds: true,
                                freeMode: false,
                            },
                            1400: {
                                slidesPerView: slidesToView.xl,
                                spaceBetween: 100,
                                centeredSlides: true,
                                centeredSlidesBounds: true,
                                freeMode: false,
                            },
                        }}
                        className="promo-slider-swiper"
                    >
                        {extendedSliderGroup.map((slider, index) => (
                            <SwiperSlide key={index}>
                                <PromoSlideVideoCard
                                    label={slider?.text_block?.label}
                                    title={slider?.text_block?.title}
                                    source={slider?.video_link}
                                    thumbnail={slider?.thumbnail}
                                    isVariantStyle={isVariantStyle}
                                    isPlaying={isPlaying}
                                    setIsPlaying={setIsPlaying}
                                    tabIndex={-1}
                                    contentType={contentType}
                                />
                            </SwiperSlide>
                        ))}

                        <Box className="promo-slider-pagination" />
                    </Swiper>

                    <HideOnMobile>
                        {/* mirrors the left arrow offsets so both sit centered in their respective gaps */}
                        <Flex
                            {...styles.heroLeftRightArrowsContainer}
                            top={{md: 'calc(50% - 60px)', lg: '50%'}}
                            sx={{
                                '@media (min-width: 1024px) and (max-width: 1431px)': {
                                    right: 'calc(27.78% - 31.33px)',
                                },
                                '@media (min-width: 1432px)': {
                                    right: 'calc(50% - 296.55px)',
                                },
                            }}
                        >
                            <Flex
                                {...styles.heroLeftRightArrows}
                                ref={nextButtonRef}
                                className={`swiper-button-next swiper-button-next-${uniqueId}`}
                                data-testid="swiper-button-next"
                                tabIndex={0}
                                role="button"
                                aria-label="swiper button next icon"
                                onClick={() => {
                                    navigationDirection.current = 'next'
                                    swiperRef.current?.slideNext()
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        navigationDirection.current = 'next'
                                        swiperRef.current?.slideNext()
                                    }
                                }}
                            >
                                <GalleryChevronRightIcon boxSize={remCalc(16)} />
                            </Flex>
                        </Flex>
                    </HideOnMobile>
                </Box>
            </Box>
        </Box>
    )
}

PromoSliderVideo.propTypes = {
    sliderGroup: PropTypes.arrayOf(
        PropTypes.shape({
            text_block: PropTypes.shape({
                label: PropTypes.string,
                title: PropTypes.string,
            }),
            video_link: PropTypes.shape({
                href: PropTypes.string,
                title: PropTypes.string,
            }),
        }),
    ),
    title: PropTypes.node,
    abTitle: PropTypes.string,
    isVariantStyle: PropTypes.bool,
    contentType: PropTypes.string,
    currentVideoSlide: PropTypes.func,
}
